/**
 * ground_textures — load the ground material v2 texture arrays and the macro
 * map from the STATIC side-channel `/ground/` (tools/asset-pipeline/
 * cook-ground-layers.mjs; no MinIO, no registry — the migrate overlay copies
 * public folders).
 *
 * The arrays are KTX2 (UASTC + zstd) and arrive as three
 * `CompressedArrayTexture`s through KTX2Loader (App's shared transcoder when
 * it has one — a second loader is a second worker pool). THE UNCOMPRESSED
 * FALLBACK LAW: a device that can sample no BC7 / ASTC / ETC2 gets UASTC
 * transcoded to RGBA8 — FOUR bytes a texel, a 2048 set would be ~540 MB — so
 * a set above 1024 that lands uncompressed is dropped and the 1024 set loaded
 * in its place.
 *
 * THE RUNG LADDER (ground, 2026-09-26 — the owner: "the ground v2 textures go up
 * to 8k"): the sets are 512 / 1024 / 2048 / 4096 / 8192 (GROUND_ARRAY_RUNGS).
 * A rung the device cannot hold drops ONE rung at a time, and every drop says
 * why (`groundTextureStats.fall`): above `maxTextureDimension2D` (WebGPU) /
 * MAX_TEXTURE_SIZE (WebGL2); a transcoder with no BC7 / ASTC / ETC2 / BC3 target
 * (the arrays would land RGBA8 — the 8192 pair would be 8.6 GB — so every rung
 * above 1024 is skipped BEFORE a byte is fetched); a file that fails to load;
 * a set that lands uncompressed anyway; and — for the 4096 / 8192 rungs — an
 * EAGER upload (`initTexture`) inside a WebGPU out-of-memory + validation error
 * scope (GL: OUT_OF_MEMORY), so "where the backend can hold it" is measured,
 * not guessed. After a good eager upload the CPU mip payload of a high rung is
 * dropped (the F15 heap law, lib/pipeline_warm.ts: 1.07 GB a 8192 array; escape
 * hatch localStorage 'atlas:keep-texture-cpu' = '1'). The ground arrays never
 * pass AssetModule's mips-first path (that is the GLB parse hook): they load
 * here, whole, and upload once.
 */
import * as THREE from 'three/webgpu';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { groundSetName, groundRungBelow, GROUND_SPLIT_SETS, groundLayerFile, GROUND_LAYER_COUNT } from './ground_layers.js';

export interface GroundTextureSet {
  set: string; res: number; layers: number;
  /** The set the knobs asked for (≠ `set` after a fall down the ladder) — a rebuild with the same ask never reloads. */
  requestedSet: string;
  a: THREE.Texture; b: THREE.Texture;
  /** Transcoded GPU format (a three constant) — RGBAFormat means uncompressed. */
  format: number;
  compressed: boolean;
  /** Estimated GPU bytes of the two arrays with mips. */
  gpuBytes: number;
  loadMs: number;
}

export const groundTextureStats = {
  set: '', res: 0, layers: 0, compressed: false, gpuMB: 0, loadMs: 0, error: '' as string, fellBack: false,
  // ground (2026-09-26): the rung asked for, one reason per rung dropped, the eager upload (high rungs) and the limit.
  requested: 0, fall: [] as string[], uploadMs: 0, maxDim: 0, format: 0, cpuStripped: false, loading: false,
};

type Loader = { loadAsync(url: string): Promise<THREE.Texture> };

function loaderFor(renderer: unknown, shared: Loader | null): { l: Loader; own: KTX2Loader | null } {
  if (shared) return { l: shared, own: null };
  const k = new KTX2Loader().setTranscoderPath('/basis/');
  k.detectSupport(renderer as never);
  return { l: k, own: k };
}

function prep(t: THREE.Texture, srgb: boolean, anisotropy: number): void {
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = anisotropy;
  t.needsUpdate = true;
}

function isCompressed(t: THREE.Texture): boolean { return t.format !== THREE.RGBAFormat && t.format !== THREE.RGBFormat; }

/**
 * A SPLIT set's array (GROUND_SPLIT_SETS — the 8192 rung): each layer is its own KTX2, transcoded alone (≈ 90 MB of
 * file + a 64 MB level in the 2 GiB transcoder heap instead of 1 GB + 805 MB), then the twelve levels are laid end
 * to end — exactly the layout KTX2Loader gives a layered file (one `data` per mip, the layers in order) — as ONE
 * CompressedArrayTexture. Three layers in flight at a time; a layer's CPU levels are released as they are copied.
 */
async function loadSplitArray(l: Loader, set: string, ab: 'a' | 'b'): Promise<THREE.Texture> {
  const n = GROUND_LAYER_COUNT, parts: THREE.CompressedTexture[] = new Array(n);
  for (let i = 0; i < n; i += 3) {
    const got = await Promise.all([i, i + 1, i + 2].filter((k) => k < n).map((k) => l.loadAsync(groundLayerFile(set, ab, k))));
    got.forEach((t, j) => { parts[i + j] = t as THREE.CompressedTexture; });
  }
  const p0 = parts[0];
  const levels = p0.mipmaps.length;
  const mipmaps: Array<{ data: Uint8Array; width: number; height: number }> = [];
  for (let lv = 0; lv < levels; lv++) {
    const m0 = p0.mipmaps[lv] as { data: Uint8Array; width: number; height: number };
    const each = m0.data.byteLength, data = new Uint8Array(each * n);
    parts.forEach((p, i) => {
      const m = p.mipmaps[lv] as { data: Uint8Array | null; width: number; height: number };
      if (!m.data || m.data.byteLength !== each || p.format !== p0.format) throw new Error(`ground ${set}/${ab}: layer ${i} level ${lv} does not match layer 0`);
      data.set(m.data, i * each); m.data = null; // release the layer's copy as it lands in the array
    });
    mipmaps.push({ data, width: m0.width, height: m0.height });
  }
  const arr = new THREE.CompressedArrayTexture(mipmaps as never, p0.image.width, p0.image.height, n, p0.format as never, p0.type);
  arr.minFilter = p0.minFilter; arr.magFilter = p0.magFilter; arr.generateMipmaps = false; arr.colorSpace = p0.colorSpace;
  for (const p of parts) { p.mipmaps.length = 0; p.dispose(); }
  arr.needsUpdate = true;
  return arr;
}

async function loadSet(l: Loader, set: string, anisotropy: number): Promise<{ a: THREE.Texture; b: THREE.Texture }> {
  // A split set loads its two arrays one after the other (each assembles ~1.07 GB at 8192); a whole-file set in parallel.
  const [a, b] = GROUND_SPLIT_SETS.has(set)
    ? [await loadSplitArray(l, set, 'a'), await loadSplitArray(l, set, 'b')]
    : await Promise.all([l.loadAsync(`/ground/${set}/ground_a.ktx2`), l.loadAsync(`/ground/${set}/ground_b.ktx2`)]);
  prep(a, true, anisotropy); prep(b, false, anisotropy);
  return { a, b };
}

type ScopeDevice = { limits?: { maxTextureDimension2D?: number }; pushErrorScope?(f: string): void; popErrorScope?(): Promise<{ message: string } | null> };
type NativeLike = { initTexture?(t: THREE.Texture): void; backend?: { device?: ScopeDevice; gl?: WebGL2RenderingContext } } | null;

/** The device's 2D texture limit and whether the transcoder has a compressed RGBA target (null = unknown). */
function deviceCaps(renderer: unknown, loader: unknown): { maxDim: number; compressed: boolean | null } {
  const r = renderer as NativeLike;
  let maxDim = 0;
  try {
    const dev = r?.backend?.device, gl = r?.backend?.gl;
    if (dev?.limits?.maxTextureDimension2D) maxDim = dev.limits.maxTextureDimension2D;
    else if (gl) maxDim = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  } catch { /* unknown — no limit applied */ }
  const wc = (loader as { workerConfig?: Record<string, boolean> } | null)?.workerConfig;
  // UASTC with alpha (the height / AO channels) transcodes to ASTC, BC7, ETC2-EAC or BC3 — anything else is RGBA8.
  const compressed = wc ? !!(wc.astcSupported || wc.bptcSupported || wc.etc2Supported || wc.dxtSupported) : null;
  return { maxDim, compressed };
}

/** Upload a high rung NOW, inside an out-of-memory + validation scope: true when the backend holds it. */
async function eagerUploadFits(renderer: unknown, a: THREE.Texture, b: THREE.Texture): Promise<{ ok: boolean; why: string; ms: number }> {
  const r = renderer as NativeLike;
  if (!r?.initTexture) return { ok: true, why: '', ms: 0 };
  const dev = r.backend?.device, gl = r.backend?.gl;
  const t0 = performance.now();
  const scoped = !!(dev?.pushErrorScope && dev.popErrorScope);
  if (scoped) { dev!.pushErrorScope!('out-of-memory'); dev!.pushErrorScope!('validation'); }
  else if (gl) { for (let i = 0; i < 16 && gl.getError() !== gl.NO_ERROR; i++) { /* drain stale errors */ } }
  let why = '';
  try { r.initTexture(a); r.initTexture(b); } catch (e) { why = `upload threw: ${String(e).slice(0, 120)}`; }
  if (scoped) {
    // Both scopes are popped even when one throws (a page torn down mid-upload rejects with "Instance dropped").
    const pop = async (): Promise<{ message: string } | null | 'lost'> => { try { return await dev!.popErrorScope!(); } catch { return 'lost'; } };
    const v = await pop(), m = await pop();
    if (v === 'lost' || m === 'lost') why ||= 'the GPU device went away during the upload';
    else {
      if (!why && m) why = `out of memory: ${m.message.slice(0, 120)}`;
      if (!why && v) why = `validation: ${v.message.slice(0, 120)}`;
    }
  } else if (gl) {
    const e = gl.getError();
    if (!why && e === gl.OUT_OF_MEMORY) why = 'out of memory (GL)';
    else if (!why && e !== gl.NO_ERROR) why = `GL error 0x${e.toString(16)}`;
  }
  return { ok: !why, why, ms: performance.now() - t0 };
}

/** The F15 heap law for an eagerly uploaded high rung: the CPU mip payload is dead weight once the GPU holds it. */
function stripCpu(t: THREE.Texture): boolean {
  try { if (localStorage.getItem('atlas:keep-texture-cpu') === '1') return false; } catch { /* no storage — strip */ }
  const ct = t as unknown as { isCompressedTexture?: boolean; mipmaps?: unknown[] };
  if (!ct.isCompressedTexture || !Array.isArray(ct.mipmaps)) return false;
  ct.mipmaps.length = 0;
  return true;
}

/** Load the array set a tier asks for (`res` 512 … 8192, `potato` = the 8-layer set), falling down THE RUNG LADDER. */
export async function loadGroundTextures(renderer: unknown, shared: Loader | null, res: number, potato: boolean, anisotropy = 4): Promise<GroundTextureSet | null> {
  const t0 = performance.now();
  const { l, own } = loaderFor(renderer, shared);
  const requestedSet = groundSetName(res, potato);
  const fall: string[] = [];
  Object.assign(groundTextureStats, { requested: potato ? 512 : res, fall, uploadMs: 0, cpuStripped: false, loading: true });
  try {
    const caps = deviceCaps(renderer, own ?? shared);
    groundTextureStats.maxDim = caps.maxDim;
    let rung = potato ? 512 : res;
    let got: { a: THREE.Texture; b: THREE.Texture } | null = null, set = requestedSet, compressed = false, uploadMs = 0, stripped = false;
    for (;;) {
      set = groundSetName(rung, potato);
      const lower = groundRungBelow(rung);
      const drop = (why: string): void => { fall.push(`${set}: ${why}`); rung = lower; };
      if (!potato && rung > 512 && caps.maxDim && rung > caps.maxDim) { drop(`above the device's ${caps.maxDim} px texture limit`); continue; }
      if (!potato && rung > 1024 && caps.compressed === false) { drop('no BC7 / ASTC / ETC2 / BC3 transcode target — it would land RGBA8'); continue; }
      try { got = await loadSet(l, set, anisotropy); }
      catch (e) { if (!potato && rung > 512) { drop(`load failed (${String(e).slice(0, 80)})`); got = null; continue; } throw e; }
      compressed = isCompressed(got.a);
      if (!compressed && !potato && rung > 1024) { got.a.dispose(); got.b.dispose(); got = null; drop('landed uncompressed (RGBA8)'); continue; }
      if (!potato && rung > 2048) {
        const fit = await eagerUploadFits(renderer, got.a, got.b);
        uploadMs += fit.ms;
        if (!fit.ok) { got.a.dispose(); got.b.dispose(); got = null; drop(fit.why); continue; }
        stripped = stripCpu(got.a) && stripCpu(got.b);
      }
      break;
    }
    if (!got) throw new Error('no ground array set loaded');
    const img = got.a.image as { width?: number; depth?: number };
    const w = img.width ?? rung, layers = img.depth ?? (potato ? 8 : 12);
    const bpp = compressed ? 1 : 4;
    const gpuBytes = Math.round(2 * layers * w * w * bpp * 4 / 3);
    const out: GroundTextureSet = { set, requestedSet, res: w, layers, a: got.a, b: got.b, format: got.a.format, compressed, gpuBytes, loadMs: performance.now() - t0 };
    Object.assign(groundTextureStats, { set, res: w, layers, compressed, gpuMB: +(gpuBytes / 1048576).toFixed(1), loadMs: Math.round(out.loadMs), error: '', fellBack: set !== requestedSet, uploadMs: Math.round(uploadMs), format: got.a.format, cpuStripped: stripped });
    return out;
  } catch (e) {
    groundTextureStats.error = String(e).slice(0, 200);
    console.warn('[ground] texture arrays unavailable — the v1 ground stays', e);
    return null;
  } finally {
    own?.dispose();
    groundTextureStats.loading = false;
  }
}

/** The 256² macro-variation map (RGBA, linear data, repeating). */
export function loadGroundMacro(): Promise<THREE.Texture | null> {
  return new THREE.TextureLoader().loadAsync('/ground/macro.png')
    .then((t) => { t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.NoColorSpace; t.generateMipmaps = true; t.needsUpdate = true; return t; })
    .catch(() => null);
}
